import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const MilestoneCarousel = ({ images, alt, testid }) => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    setSnaps(embla.scrollSnapList());
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    onSelect();
    return () => {
      embla.off("select", onSelect);
      embla.off("reInit", onSelect);
    };
  }, [embla, onSelect]);

  const multi = images.length > 1;

  return (
    <div className="relative" data-testid={testid}>
      <div className="overflow-hidden rounded-sm bg-ink" ref={emblaRef}>
        <div className="flex">
          {images.map((src, i) => (
            <div className="min-w-0 shrink-0 grow-0 basis-full" key={i}>
              <div className="flex h-[400px] sm:h-[480px] lg:h-[540px] items-center justify-center p-2">
                <img
                  src={src}
                  alt={`${alt} — photo ${i + 1}`}
                  data-testid={`${testid}-slide-${i}`}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {multi && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid={`${testid}-dots`}>
            {snaps.map((_, i) => (
              <button
                key={i}
                data-testid={`${testid}-dot-${i}`}
                onClick={() => embla && embla.scrollTo(i)}
                aria-label={`Go to photo ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === selected ? "w-6 bg-ink" : "w-1.5 bg-inkmuted/40 hover:bg-inkmuted"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="mr-2 font-mono text-xs text-inkmuted">
              {String(selected + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </span>
            <button
              data-testid={`${testid}-prev`}
              onClick={() => embla && embla.scrollPrev()}
              aria-label="Previous photo"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-ink text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              data-testid={`${testid}-next`}
              onClick={() => embla && embla.scrollNext()}
              aria-label="Next photo"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-ink text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
